<template>
  <div class="performance-by-location">
    <div class="performance-by-location__heading">
      <h2 class="global-h4 performance-by-location__title">Performance by location</h2>

      <div class="performance-by-location__legend">
        <span class="performance-by-location__legend-chip">
          <span class="performance-by-location__dot performance-by-location__dot--reach"></span>
          Reach
        </span>
        <span class="performance-by-location__legend-chip">
          <span class="performance-by-location__dot performance-by-location__dot--engagements"></span>
          Engagements
        </span>
      </div>
    </div>

    <p v-if="!topRows.length" class="performance-by-location__empty">No location metrics yet</p>

    <div v-else class="performance-by-location__list">
      <div v-for="row in topRows" :key="row.locationId" class="performance-by-location__row">
        <div class="performance-by-location__location">
          <LocationAvatar :src="row.icon" :name="row.name" :size="32" />
          <span class="performance-by-location__name">{{ row.name }}</span>
        </div>

        <div class="performance-by-location__bars">
          <div class="performance-by-location__bar-line">
            <div class="performance-by-location__track">
              <div class="performance-by-location__fill performance-by-location__fill--reach" :style="{ width: `${barPercent(row.reach)}%` }"></div>
            </div>
            <div class="performance-by-location__value">{{ formatCount(row.reach) }}</div>
          </div>

          <div class="performance-by-location__bar-line">
            <div class="performance-by-location__track">
              <div class="performance-by-location__fill performance-by-location__fill--engagements" :style="{ width: `${barPercent(row.engagements)}%` }"></div>
            </div>
            <div class="performance-by-location__value">{{ formatCount(row.engagements) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import NumberFormatting from "@logic/NumberFormatting";

// Components
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";

// Types
import type { LocationSocialMetricsRow } from "@contracts/socialReports";

const props = withDefaults(
  defineProps<{
    rows: LocationSocialMetricsRow[];
    limit?: number;
  }>(),
  { limit: 5 }
);

/** Rows arrive sorted by reach desc from the backend; keep the strongest few and drop empty ones. */
const topRows = computed<LocationSocialMetricsRow[]>(() => props.rows.filter(r => r.reach > 0 || r.engagements > 0).slice(0, props.limit));

// Both bars share one scale so reach and engagements stay comparable across rows.
const maxValue = computed<number>(() => Math.max(0, ...topRows.value.map(r => Math.max(r.reach, r.engagements))));

function barPercent(value: number): number {
  if (maxValue.value <= 0 || value <= 0) return 0;
  return Math.max(3, Math.round((100 * value) / maxValue.value));
}

function formatCount(value: number): string {
  return NumberFormatting.abbreviatedFormat(value) || "0";
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Performance by location ====================================================
.performance-by-location {
  height: 100%;
  box-sizing: border-box;
  padding: 22px 24px;
  border: 1px solid #e4e7ec;
  border-radius: 11px;
  background: #fff;

  &__heading {
    margin-bottom: 18px;
    gap: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
  }

  &__legend {
    gap: 14px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__legend-chip {
    padding: 3px 10px 3px 8px;
    border-radius: 100px;
    display: inline-flex;
    align-items: center;
    background: #f4f6f8;
    color: #667085;
    font: 400 13px/17px sans-serif;
    white-space: nowrap;
  }

  &__dot {
    width: 9px;
    height: 9px;
    margin-right: 7px;
    border-radius: 100px;

    &--reach {
      background: #058587;
    }

    &--engagements {
      background: #8b7fe8;
    }
  }

  &__empty {
    margin: 0;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__list {
    gap: 18px;
    display: flex;
    flex-direction: column;
  }

  &__row {
    gap: 12px;
    display: flex;
    align-items: center;
  }

  &__location {
    width: 180px;
    gap: 10px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__name {
    min-width: 0;
    color: #102f41;
    font: 500 14px/17px sans-serif;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__bars {
    min-width: 0;
    gap: 6px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &__bar-line {
    gap: 10px;
    display: flex;
    align-items: center;
  }

  &__track {
    height: 8px;
    border-radius: 100px;
    overflow: hidden;
    background: #eef1f4;
    flex-grow: 1;
  }

  &__fill {
    height: 100%;
    border-radius: 100px;
    transition: width 0.2s ease-in-out;

    &--reach {
      background: #058587;
    }

    &--engagements {
      background: #8b7fe8;
    }
  }

  &__value {
    width: 54px;
    flex-shrink: 0;
    color: #102f41;
    font: 600 14px/17px $font-family;
    text-align: right;
  }
}

// tablet + mobile ------------------------
@media (max-width: $tablet-max-width) {
  .performance-by-location {
    &__row {
      gap: 8px;
      flex-direction: column;
      align-items: stretch;
    }

    &__location {
      width: auto;
    }

    &__bars {
      width: 100%;
    }
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .performance-by-location {
    padding: 18px 15px;
  }
}
</style>
